// UTILS
import { nextTick } from "vue"
import { storeToRefs } from "pinia"
import { setup, createActor, fromPromise } from "xstate"
import router from "@/router"
import { useCookies } from "@vueuse/integrations/useCookies.js"
import { COOKIES } from "@/config/constants"
import { useSessionStorage } from "@/helpers/useSessionStorage"

// TYPES
import { type GlobalMachineEvent } from "@/machines/types"
import type { DriveMachineEventType } from "./types"

// STORES
import { useUserStore } from "@/stores/user"
import { useI18nStore } from "@/stores/i18n"
import { useAnswersStore } from "@/features/drive/store/answers.store"
import { useQuestionsStore } from "@/features/drive/store/questions.store"

// MACHINE
import { globalActor } from "@/machines/globalMachine"
import { useHelpStore } from "@/components/af/Help/help.store"
import { withAssessmentLoadingMinDuration } from "@/features/assessment-loading/assessmentLoadingDuration"

// Loader for Drive datas
// to redirect to Intro or WelcomeBack
const loadDriveData = fromPromise(() =>
  withAssessmentLoadingMinDuration("DRIVE", async () => {
    const { fetchDrive } = useAnswersStore()
    const data = await fetchDrive()

    const { fetchQuestions, fetchAudios } = useQuestionsStore()
    const { questionsLength } = storeToRefs(useQuestionsStore())

    const { showLang } = storeToRefs(useI18nStore())

    const assessmentSummary = useUserStore().assessmentSummary.assessments.drive
    const isFinisedAndNotPassed =
      data?.status === "finish" && assessmentSummary?.not_passed

    // Legacy, the test has been not_passed and considered with status finish in assessment summary (old) but not in workflow and next_assessment (new)
    let assessmentStatus = isFinisedAndNotPassed ? "todo" : data?.status

    // The language can only be changed as long as the test is not started
    showLang.value = assessmentStatus !== "inprogress"

    // status can be "todo", "inprogress" or "finish"
    switch (assessmentStatus) {
      case "todo":
        await fetchQuestions()
        await fetchAudios()
        return { status: "new" }

      case "inprogress":
        await fetchQuestions()
        await fetchAudios()
        if (data.data.items.length < questionsLength.value) {
          return { status: "showQuestions" }
        } else {
          return { status: "showLoadResults" }
        }

      case "redirect-contact":
        globalActor.send({ type: "GLOBAL.GUEST.CONTACTSUPPORT" })
        return

      case "thank-you":
        router.replace({ name: "thanksATS" })
        return

      case "finish":
        return { status: "showFinal" }

      default:
        await fetchQuestions()
        await fetchAudios()
        return { status: "new" }
    }
  })
)

export const driveMachine = setup({
  actors: {
    loadDriveData,
  },
  types: {} as {
    events: DriveMachineEventType
  },
  actions: {},
}).createMachine({
  id: "driveMachine",
  initial: "loading",
  states: {
    loading: {
      entry: [
        () => {
          if (router.currentRoute.value.name !== "driveLoading") {
            router.replace({ name: "driveLoading" })
          }
        },
      ],
      invoke: {
        id: "loadDrive",
        src: "loadDriveData",
        onDone: [
          {
            guard: ({ event }) =>
              (event.output as { status: string })?.status === "new",
            target: "tuto",
          },
          {
            guard: ({ event }) =>
              (event.output as { status: string })?.status === "showQuestions",
            target: "welcomeBack",
          },
          {
            guard: ({ event }) =>
              (event.output as { status: string })?.status ===
              "showLoadResults",
            target: "loadResults",
          },
          {
            guard: ({ event }) =>
              (event.output as { status: string })?.status === "showFinal",
            target: "final",
          },
        ],
      },
    },
    welcomeBack: {
      entry: [() => router.replace({ name: "driveTest" })],
      on: {
        done: { target: "test" },
      },
    },
    tuto: {
      entry: [() => router.replace({ name: "driveTuto" })],
      on: {
        done: { target: "test" },
      },
    },
    test: {
      entry: [
        () => {
          if (router.currentRoute.value.name !== "driveTest") {
            router.replace({ name: "driveTest" })
          }
        },
      ],
      on: {
        done: { target: "loadResults" },
      },
    },
    loadResults: {
      entry: [() => router.replace({ name: "driveLoadResults" })],
      on: {
        done: { target: "final" },
      },
    },
    final: {
      type: "final",
    },
  },
})

export let driveActor = createActor(driveMachine)

const driveActorObserver = {
  async complete() {
    const { isFromInt } = storeToRefs(useUserStore())
    const { getSessionItem } = useSessionStorage()
    const { fetchNextAssessment } = useUserStore()
    const { nextAssessmentName } = storeToRefs(useUserStore())
    const { af_workflow_uuid } = storeToRefs(useHelpStore())

    await fetchNextAssessment(af_workflow_uuid.value ?? null)

    await nextTick()

    if (isFromInt.value && getSessionItem("goto_param")) {
      const didRedirect = await redirectForInt()
      if (didRedirect) return
    }

    if (nextAssessmentName.value) {
      globalActor.send({
        type: `GLOBAL.REDIRECT.${nextAssessmentName.value.toUpperCase()}` as GlobalMachineEvent,
      })
    } else {
      useCookies().remove(COOKIES.WORKFLOW_UUID, COOKIES.OPTIONS)
      useCookies().remove(COOKIES.PM_UUID, COOKIES.OPTIONS)
      useCookies().remove(COOKIES.COMPANY_UUID, COOKIES.OPTIONS)
      af_workflow_uuid.value = ""

      const didRedirect = await redirectForInt()
      if (didRedirect) return

      globalActor.send({ type: "GLOBAL.DRIVE.DONE" })
    }
  },
}

driveActor.subscribe(driveActorObserver)

async function redirectForInt(): Promise<boolean> {
  const { isFromInt } = storeToRefs(useUserStore())
  const { getSessionItem, deleteSessionItem } = useSessionStorage()

  if (isFromInt.value) {
    if (getSessionItem("goto_param")) {
      deleteSessionItem("goto_param")
    }

    if (getSessionItem("redirect_url")) {
      let redirectUrl = getSessionItem("redirect_url") as string
      deleteSessionItem("redirect_url")
      window.location.href = redirectUrl
      return true
    } else {
      await router.push({ name: "thanksATS" })
      return true
    }
  }

  return false
}

/**
 * Observateurs extérieurs, réabonnés à chaque acteur neuf.
 *
 * Sans ça, un abonné qui garde la référence de l'acteur — le store d'aide, qui
 * en tire le nom de l'assessment courant, donc les CTA du header — resterait
 * branché sur l'acteur remplacé et deviendrait sourd.
 */
const driveActorObservers: Array<(_snapshot: unknown) => void> = []

export function subscribeToDriveActor(observer: (_snapshot: unknown) => void) {
  driveActorObservers.push(observer)
  driveActor.subscribe(observer)
}

let hasDriveActorStarted = false

/**
 * XState ignore `start()` sur un acteur déjà démarré (ou arrivé à son état
 * final) : aucune action d'entrée ne rejoue, la machine ne réapplique donc
 * jamais sa route et l'utilisateur reste sur la route parente nue, écran
 * blanc. Le cas se produit dès qu'on entre deux fois dans l'assessment au
 * cours de la même page — sortie par « non passé », ou renvoi dessus par le
 * workflow. On repart donc d'un acteur neuf.
 *
 * Mais UNIQUEMENT en entrant vraiment dans l'assessment, c'est-à-dire sur la
 * route parente nue, celle par laquelle la machine globale y fait entrer. Le
 * composant racine est aussi remonté sans qu'on change d'assessment — App.vue
 * reconstruit tout `main` quand la langue change — et repartir de zéro là
 * relancerait le test depuis son écran de chargement.
 */
export function startDriveActor() {
  const isEnteringAssessment = router.currentRoute.value.name === "drive"

  if (hasDriveActorStarted && isEnteringAssessment) {
    driveActor.stop()
    driveActor = createActor(driveMachine)
    driveActor.subscribe(driveActorObserver)
    for (const observer of driveActorObservers) driveActor.subscribe(observer)
  }

  hasDriveActorStarted = true
  driveActor.start()
}
